import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { isType } from '@sorare/core/src/lib/gql';

import { FeaturedMissionCard } from '#src/components/missions/FeaturedMissionCard/index.tsx';
import { SetCollectMissionsDialog } from '#src/components/missions/SetCollectMissionsDialog/index.tsx';
import { SetCollectionTask } from '#src/components/missions/SetCollectionTask/index.tsx';

import { TaskClaimedDialogs } from '../TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '../TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import type { FeaturedCollectionTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: FeaturedCollectionTask_taskInterface;
};

export const FeaturedCollectionTask = ({ task }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  return (
    <>
      <FeaturedMissionCard color="var(--c-inventory-yellow)">
        <SetCollectionTask
          task={task}
          onClaim={async () => {
            if (isType(task, 'TasksTrack') && task.currentStep) {
              onClaim(task.currentStep);
            }
          }}
          loading={loadingTaskId === task.id}
          onClick={null}
        />
        <Button
          size="medium"
          color="quaternary"
          onClick={() => setDialogOpen(true)}
        >
          <FormattedMessage defaultMessage="View all Collect Missions" />
        </Button>
      </FeaturedMissionCard>
      <SetCollectMissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
        }}
      />
    </>
  );
};

FeaturedCollectionTask.fragments = {
  taskInterface: gql`
    fragment FeaturedCollectionTask_taskInterface on TaskInterface {
      id
      ...SetCollectionTask_taskInterface
      ... on TasksTrack {
        id
        currentStep {
          id
          ...TaskClaimedDialogs_taskInterface
        }
      }
    }
    ${SetCollectionTask.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<FeaturedCollectionTask_taskInterface>,
};
